import React, { useState } from 'react';

export interface StrictnessScheduleItem {
  id: string;
  name: string;
  timeWindow: string;
  category: string;
  tier: 'gentle_nudge' | 'friction_delay' | 'strict_lock';
  allowOverride: boolean;
  isActive: boolean;
}

export const StrictnessTierScheduler: React.FC = () => {
  const [schedules, setSchedules] = useState<StrictnessScheduleItem[]>([
    {
      id: 'sched_1',
      name: '🌙 Nighttime High-Risk Speculation Guard',
      timeWindow: '22:00 - 06:00',
      category: 'Financial / High-Risk Speculation',
      tier: 'strict_lock',
      allowOverride: false,
      isActive: true,
    },
    {
      id: 'sched_2',
      name: '☀️ Workday Mindful Media Friction',
      timeWindow: '09:00 - 17:00',
      category: 'Mindful Media Consumption',
      tier: 'friction_delay',
      allowOverride: true,
      isActive: true,
    },
    {
      id: 'sched_3',
      name: '🛍️ Late-Night Shopping Cooling-Off',
      timeWindow: '23:00 - 05:00',
      category: 'Mindful Spending & Budgeting',
      tier: 'strict_lock',
      allowOverride: false,
      isActive: true,
    },
    {
      id: 'sched_4',
      name: '🌿 Weekend Grounding Routine',
      timeWindow: 'All Day (Sat-Sun)',
      category: 'Digital Screen-Time Balance',
      tier: 'gentle_nudge',
      allowOverride: true,
      isActive: false,
    },
  ]);

  const toggleActive = (id: string) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s))
    );
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'strict_lock':
        return { label: '🔒 Strict Lockout', bg: '#FEE2E2', color: '#DC2626' };
      case 'friction_delay':
        return { label: '⏳ Mindful Friction', bg: '#FEF3C7', color: '#D97706' };
      case 'gentle_nudge':
      default:
        return { label: '🌿 Gentle Nudge', bg: '#E8F5E9', color: '#16A34A' };
    }
  };

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
      border: '1px solid #E2E8F0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{
          backgroundColor: '#E0F2FE',
          color: '#0284C7',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
        }}>
          ⏰
        </div>
        <div>
          <h3 style={{ margin: 0, color: '#2D3436', fontSize: '18px', fontWeight: 600 }}>
            Scheduled Strictness Tiers
          </h3>
          <p style={{ margin: '2px 0 0', color: '#636E72', fontSize: '13px' }}>
            Adaptive day vs night behavioral protection windows
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {schedules.map((schedule) => {
          const badge = getTierBadge(schedule.tier);
          return (
            <div
              key={schedule.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: schedule.isActive ? '#F8FAFC' : '#F1F5F9',
                border: `1px solid ${schedule.isActive ? '#CBD5E1' : '#E2E8F0'}`,
                opacity: schedule.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <input
                  type="checkbox"
                  checked={schedule.isActive}
                  onChange={() => toggleActive(schedule.id)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#4A7C59' }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#1E293B' }}>
                    {schedule.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                    {schedule.category} • <span style={{ fontWeight: 600 }}>{schedule.timeWindow}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  backgroundColor: badge.bg,
                  color: badge.color,
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 700,
                }}>
                  {badge.label}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: schedule.allowOverride ? '#059669' : '#DC2626',
                  fontWeight: 600,
                }}>
                  {schedule.allowOverride ? '✓ Override Allowed' : '🚫 No Override'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
